import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { CalendarDays, ArrowLeftRight, CheckCircle, XCircle, Clock, Send, FileText } from 'lucide-react';

const statusColors = { pending: 'bg-amber-500/20 text-amber-400', approved: 'bg-emerald-500/20 text-emerald-400', rejected: 'bg-red-500/20 text-red-400' };
const leaveTypeColors = { annual: 'bg-blue-500/20 text-blue-400', sick: 'bg-red-500/20 text-red-400', personal: 'bg-purple-500/20 text-purple-400' };

export default function LeavePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('leave');
  const [leaves, setLeaves] = useState([]);
  const [swaps, setSwaps] = useState([]);
  const [balances, setBalances] = useState({});
  const [leaveForm, setLeaveForm] = useState({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });
  const [leaveDialog, setLeaveDialog] = useState(false);
  const [actionDialog, setActionDialog] = useState(null);
  const [actionNotes, setActionNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const isManager = user?.role === 'manager' || user?.role === 'director';

  const fetchData = useCallback(async () => {
    try {
      const [lRes, sRes, bRes] = await Promise.all([
        api.get('/staff/leave/requests'),
        api.get('/staff/shift-swaps'),
        api.get('/staff/leave/balances')
      ]);
      setLeaves(lRes.data || []);
      setSwaps(sRes.data || []);
      setBalances(bRes.data || {});
    } catch (err) { console.error('[LeavePage.js] error:', err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const submitLeave = async () => {
    if (!leaveForm.start_date || !leaveForm.end_date) { toast.error('Select dates'); return; }
    setSaving(true);
    try {
      await api.post('/staff/leave/request', leaveForm);
      toast.success('Leave request submitted! Manager notified.');
      setLeaveDialog(false);
      setLeaveForm({ leave_type: 'annual', start_date: '', end_date: '', reason: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const actionLeave = async (id, action) => {
    try {
      await api.put(`/staff/leave/requests/${id}`, { action, manager_notes: actionNotes });
      toast.success(`Leave ${action}d`);
      setActionDialog(null);
      setActionNotes('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const actionSwap = async (id, action) => {
    try {
      await api.put(`/staff/shift-swaps/${id}`, { action });
      toast.success(`Swap ${action}d`);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const formatDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });

  const pendingLeaves = leaves.filter(l => l.status === 'pending');
  const pendingSwaps = swaps.filter(s => s.status === 'pending');

  const tabs = [
    { key: 'leave', label: 'Leave Requests', icon: CalendarDays, count: isManager ? pendingLeaves.length : 0 },
    { key: 'swaps', label: 'Shift Swaps', icon: ArrowLeftRight, count: isManager ? pendingSwaps.length : 0 },
  ];

  return (
    <div className="animate-fadeIn">
      {/* Leave Balances */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        {['annual', 'sick', 'personal'].map(type => {
          const total = balances[type] || 0;
          const used = balances[`used_${type}`] || 0;
          const remaining = total - used;
          return (
            <div key={type} className="bg-white/[0.03] border border-white/5 rounded-sm p-4 text-center">
              <p className="text-2xl font-sans text-white font-semibold">{remaining}</p>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-sans">{type} days left</p>
              <p className="text-[10px] text-zinc-600 font-sans">{used} used / {total} total</p>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-1.5">
          {tabs.map(t => (
            <Button key={t.key} size="sm" variant={tab === t.key ? 'default' : 'ghost'} onClick={() => setTab(t.key)}
              className={`h-8 rounded-sm font-sans text-xs ${tab === t.key ? 'bg-gold/10 text-gold border border-gold/20' : 'text-zinc-500 hover:text-zinc-300'}`}
              data-testid={`leave-tab-${t.key}`}>
              <t.icon className="w-3.5 h-3.5 mr-1.5" /> {t.label}
              {t.count > 0 && <Badge className="bg-red-500/20 text-red-400 border-0 text-[10px] ml-1.5">{t.count}</Badge>}
            </Button>
          ))}
        </div>
        <Button size="sm" onClick={() => setLeaveDialog(true)} className="h-8 bg-gold text-black font-bold rounded-sm font-sans text-xs" data-testid="request-leave-btn">
          <Send className="w-3 h-3 mr-1" /> Request Leave
        </Button>
      </div>

      {/* LEAVE TAB */}
      {tab === 'leave' && (
        <div className="space-y-2">
          {leaves.length === 0 ? (
            <p className="text-zinc-600 font-sans text-sm text-center py-8">No leave requests</p>
          ) : leaves.map(leave => (
            <div key={leave.id} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm" data-testid={`leave-${leave.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    {isManager && <span className="text-white font-sans text-sm font-medium">{leave.user_name}</span>}
                    <Badge className={`${leaveTypeColors[leave.leave_type]} border-0 font-sans text-[10px]`}>{leave.leave_type}</Badge>
                    <Badge className={`${statusColors[leave.status]} border-0 font-sans text-[10px]`}>{leave.status}</Badge>
                  </div>
                  <p className="text-zinc-400 font-sans text-sm">{formatDate(leave.start_date)} — {formatDate(leave.end_date)} ({leave.days} day{leave.days > 1 ? 's' : ''})</p>
                  {leave.reason && <p className="text-zinc-500 font-sans text-xs mt-1">{leave.reason}</p>}
                  {leave.manager_notes && <p className="text-zinc-400 font-sans text-xs mt-1 italic">Manager: {leave.manager_notes}</p>}
                </div>
                {isManager && leave.status === 'pending' && (
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => actionLeave(leave.id, 'approve')} className="h-7 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-sm font-sans text-xs" data-testid={`approve-leave-${leave.id}`}>
                      <CheckCircle className="w-3 h-3 mr-1" /> Approve
                    </Button>
                    <Button size="sm" onClick={() => { setActionDialog(leave); setActionNotes(''); }} className="h-7 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-sm font-sans text-xs" data-testid={`reject-leave-${leave.id}`}>
                      <XCircle className="w-3 h-3 mr-1" /> Reject
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* SWAPS TAB */}
      {tab === 'swaps' && (
        <div className="space-y-2">
          {swaps.length === 0 ? (
            <p className="text-zinc-600 font-sans text-sm text-center py-8">No shift swap requests</p>
          ) : swaps.map(swap => (
            <div key={swap.id} className="p-4 bg-white/[0.03] border border-white/5 rounded-sm" data-testid={`swap-${swap.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`${statusColors[swap.status]} border-0 font-sans text-[10px]`}>{swap.status}</Badge>
                  </div>
                  <p className="text-white font-sans text-sm">
                    <span className="text-gold">{swap.requester_name}</span>
                    <ArrowLeftRight className="w-3 h-3 inline mx-2 text-zinc-500" />
                    <span className="text-blue-400">{swap.target_name}</span>
                  </p>
                  <p className="text-zinc-400 font-sans text-xs mt-1">{swap.shift_date} | {swap.shift_time}</p>
                  {swap.reason && <p className="text-zinc-500 font-sans text-xs mt-1">{swap.reason}</p>}
                </div>
                {isManager && swap.status === 'pending' && (
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => actionSwap(swap.id, 'approve')} className="h-7 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 rounded-sm font-sans text-xs" data-testid={`approve-swap-${swap.id}`}>
                      <CheckCircle className="w-3 h-3" />
                    </Button>
                    <Button size="sm" onClick={() => actionSwap(swap.id, 'reject')} className="h-7 bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 rounded-sm font-sans text-xs" data-testid={`reject-swap-${swap.id}`}>
                      <XCircle className="w-3 h-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Leave Request Dialog */}
      <Dialog open={leaveDialog} onOpenChange={setLeaveDialog}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-white">Request Leave</DialogTitle></DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Leave Type</Label>
              <Select value={leaveForm.leave_type} onValueChange={v => setLeaveForm(p => ({ ...p, leave_type: v }))}>
                <SelectTrigger className="bg-transparent border border-white/10 text-white font-sans" data-testid="leave-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-bg-secondary border border-white/10">
                  <SelectItem value="annual">Annual Leave</SelectItem>
                  <SelectItem value="sick">Sick Leave</SelectItem>
                  <SelectItem value="personal">Personal Leave</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Start Date</Label>
                <Input type="date" value={leaveForm.start_date} onChange={e => setLeaveForm(p => ({ ...p, start_date: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="leave-start-date" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">End Date</Label>
                <Input type="date" value={leaveForm.end_date} onChange={e => setLeaveForm(p => ({ ...p, end_date: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" data-testid="leave-end-date" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Reason</Label>
              <Input value={leaveForm.reason} onChange={e => setLeaveForm(p => ({ ...p, reason: e.target.value }))} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Optional reason" data-testid="leave-reason" />
            </div>
            <Button onClick={submitLeave} disabled={saving} className="w-full bg-gold text-black font-bold rounded-sm font-sans" data-testid="submit-leave-btn">
              {saving ? <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" /> : 'Submit Request'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reject with Notes Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={v => { if (!v) setActionDialog(null); }}>
        <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-sm">
          <DialogHeader><DialogTitle className="font-serif text-white">Reject Leave</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <Input value={actionNotes} onChange={e => setActionNotes(e.target.value)} className="bg-transparent border border-white/10 text-white font-sans" placeholder="Reason for rejection" data-testid="reject-notes" />
            <Button onClick={() => { actionLeave(actionDialog.id, 'reject'); }} className="w-full bg-red-500/20 text-red-400 border border-red-500/20 rounded-sm font-sans" data-testid="confirm-reject-btn">Reject</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
